import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import makeComponentTrashable from 'trashable-react';
import { createFirstUser, fetchMigrationInfoText } from 'Shared/util/web-api';
import useTranslationContext from 'Contexts/translation-context';
import InputText from 'Shared/components/input-text';
import Button from 'Shared/components/button';
import InformationModal from 'Shared/components/modals/information-modal';
import TextLink from 'Shared/components/text-link';
import { getLink } from 'Shared/navigation/login/routes';
import { useModal, useTranslation } from 'Shared/util/custom-hooks';
import styles from '../login.css';


const UpdateAccountForm = ({
  registerPromise,
  dispatchStatusMessage,
}) => {
  const [firstName, setFirstName] = useState('');
  const [validFirstName, setValidFirstName] = useState(true);
  const [lastName, setLastName] = useState('');
  const [validLastName, setValidLastName] = useState(true);
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [migrationInfoText, setMigrationInfoText] = useState({ title: '', body: '' });
  const location = useLocation();
  const history = useHistory();
  const { translationData } = useTranslationContext();
  const { isModalOpen, openModal, closeModal } = useModal();
  const t = useTranslation(x => x.login);

  const handleRedirect = () => {
    if (!location.state || !location.state.ugId || !location.state.token) {
      const redirectStatusMessage = {
        type: 'FAILED',
        message: t.somethingWentWrong,
      };
      history.push({
        pathname: getLink('login'),
        state: {
          message: redirectStatusMessage,
        },
      });
    }
  };

  useEffect(() => {
    handleRedirect();
  }, []);

  useEffect(() => {
    registerPromise(fetchMigrationInfoText())
      .then((result) => {
        if (result) {
          setMigrationInfoText(result);
        }
      });
  }, []);

  const userObject = {
    firstName,
    lastName,
    companies: [{ ugId: location.state ? location.state.ugId : '', userEmail: email.toLowerCase() }],
  };

  const userIsCreated = (res) => res.length > 0;

  const submitForm = (e) => {
    setIsLoading(true);
    registerPromise(createFirstUser(location.state ? location.state.token : '', userObject, translationData.languageCode))
      .then((response) => {
        setIsLoading(false);
        if (userIsCreated(response)) {
          const redirectStatusMessage = {
            type: 'SUCCEEDED',
            message: t.activationLinkForYourNewAccount,
          };
          history.push({
            pathname: getLink('login'),
            state: {
              message: redirectStatusMessage,
            },
          });
        } else {
          const redirectStatusMessage = {
            type: 'SUCCEEDED',
            message: t.youCanNowUseYourEmailAccount,
          };
          history.push({
            pathname: getLink('login'),
            state: {
              message: redirectStatusMessage,
            },
          });
        }
      })
      .catch(() => {
        dispatchStatusMessage({
          type: 'FAILED',
          message: t.couldNotActivateAdministratorAccount,
        });
        setIsLoading(false);
      });

    e.preventDefault();
  };

  const isSubmitEnabled = validFirstName && validLastName && validEmail;
  const submitText = t.createAccount;

  return (
    <>
      <form onSubmit={(e) => submitForm(e)}>
        <p>{t.toContinueUsingMyPagesYourCompanyNeeds}</p>
        <TextLink
          text={t.clickHereToReadMore}
          link={openModal}
          className={styles.readMoreTextLink}
        />
        <>
          <InputText
            inputType="firstname"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            setEnabledSubmit={(bool) => setValidFirstName(bool)}
            isRequired
          />
          <InputText
            inputType="lastname"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            setEnabledSubmit={(bool) => setValidLastName(bool)}
            isRequired
          />
          <InputText
            inputType="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            setEnabledSubmit={(bool) => setValidEmail(bool)}
            isRequired
          />
        </>
        <Button
          className={styles.submitButton}
          buttonType="action-primary"
          isLoading={isLoading}
          text={submitText}
          attributes={{ type: 'submit', disabled: !isSubmitEnabled }}
        />
      </form>

      <InformationModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        headerText={migrationInfoText && migrationInfoText.title}
        contentText={migrationInfoText && migrationInfoText.body}
      />
    </>
  );
};

UpdateAccountForm.propTypes = {
  registerPromise: PropTypes.func.isRequired,
  dispatchStatusMessage: PropTypes.func.isRequired,
};

export default makeComponentTrashable(UpdateAccountForm);
